import Combobox from "@/components/custom/Combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { crmSystem } from "@/constants/crm/_shared/systems";
import { QuestionFormSchemaType } from "@/features/question/components/organisms/QuestionForm/QuestionForm.types";
import { QUESTION_BASE_TYPE, QUESTION_CONDITIONAL_TYPE } from "@/lib/schema/configuration/question_conditional";
import { Tables } from '@/lib/supabase/supabase_types';
import { Where, WhereField } from "@/types/crm/configuration/question_conditionals";
import { X } from "lucide-react";
import { useMemo } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";

const airtableConditionalQuestionBaseTypeMap: Record<string, typeof QUESTION_BASE_TYPE[number]> = {
    'singleLineText': 'text',
    'multilineText': 'text',
    'number': 'number',
    'currency': 'number',
    'percent': 'number',
    'date': 'text',
    'dateTime': 'text',
    'dateTimezone': 'text',
    'duration_hours': 'text',
    'duration_days': 'text',
    'multipleSelects': 'list',
    'checkbox': 'list',
    'singleSelect': 'list',
    'rating': 'number',
    'url': 'text',
    'email': 'text',
    'phoneNumber': 'text',
}

const salesforceConditionalQuestionBaseTypeMap: Record<string, typeof QUESTION_BASE_TYPE[number]> = {
    'text': 'text',
    'textarea': 'text',
    'number': 'number',
    'currency': 'number',
    'slider': 'number',
    'date': 'text',
    'picklist': 'list',
    'boolean': 'boolean',
    'picklist_multiple': 'list',
    'radio': 'list',
    'checkbox': 'list',
    'rank': 'number',
    'file': 'boolean',
    'pick_group_rank': 'list',
}

const conditionalsQuestionBaseTypeMaps = {
    'airtable': airtableConditionalQuestionBaseTypeMap,
    'salesforce': salesforceConditionalQuestionBaseTypeMap,
    'collaborate': salesforceConditionalQuestionBaseTypeMap
} satisfies Record<crmSystem, unknown>

const conditionalsQuestionBaseConditionsTypesMaps = {
    "number": [
        "equals",
        "not-equals",
        "greater-than",
        "less-than",
        "greater-than-equals",
        "less-than-equals",
        "reg-exp",
    ],
    "text": [
        "equals",
        "not-equals",
        "contains",
        "not-contains",
        "reg-exp",
    ],
    "list": [
        // "equals",
        // "not-equals",
        "contains",
        "not-contains",
    ],
    "boolean": [
        "equals",
        "not-equals",
    ]
} satisfies Record<typeof QUESTION_BASE_TYPE[number], typeof QUESTION_CONDITIONAL_TYPE[number][]>

type Props = {
    groupIndex: number;
    conditionIndex: number;
    form: UseFormReturn<QuestionFormSchemaType>;
    remove: (conditionIndex: number) => void;
    questions: Tables<'question'>[];
    crmSystem?: crmSystem;
}

export const ConditionFields = ({
    groupIndex,
    conditionIndex,
    form,
    remove,
    questions,
    crmSystem,
}: Props) => {
    const getConditionalKey = (whereData: Where | WhereField | undefined) => {
        if (whereData === undefined) return [];
        const allKeys = Object.keys(whereData);
        return allKeys.filter(key => key !== 'and' && key !== 'or');
    };
    const firstQuestionId = getConditionalKey(form.watch(`selected_configuration.conditionals.where.or.0.and.0`))?.[0];

    const questionGroups = useWatch({
        control: form.control,
        name: `selected_configuration.conditionals.where.or`,
    }) as Where[] | undefined;

    const selectedQuestionsCount = useMemo(() => {
        return questionGroups?.reduce((count, group) => {
            return count + (group.and?.length || 0);
        }, 0) || 0;
    }, [questionGroups]);

    // const selectedQuestionId = getConditionalKey(form.watch(`selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}`))?.[0];
    const selectedQuestionId = firstQuestionId;
    const selectedType = getConditionalKey(form.watch(`selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}.${selectedQuestionId}`))?.[0];

    const questionType = useMemo(() => {
        const matched = questions.find((q) => q.id === selectedQuestionId);
        if (matched && crmSystem) {
            return conditionalsQuestionBaseTypeMaps[crmSystem]?.[matched.field_type];
        }
        return undefined;
    }, [selectedQuestionId, crmSystem, questions]);
    const typeOptions = questionType && conditionalsQuestionBaseConditionsTypesMaps[questionType]
        ? conditionalsQuestionBaseConditionsTypesMaps[questionType]
        : [];

    const selectedQuestion = useMemo(() => {
        return questions.find((question) => question.id === selectedQuestionId);
    }, [selectedQuestionId, questions]);

    const filteredQuestions = useMemo(() => {
        if (firstQuestionId && selectedQuestionsCount > 1) {
            return questions.filter((question) => question.id === firstQuestionId);
        }
        return questions;
    }, [questions, firstQuestionId, selectedQuestionsCount]);
    return (
        <div className="flex items-center gap-2">
            <Button size="action" variant="ghost" type="button" onClick={() => remove(conditionIndex)}>
                <X size="16" />
            </Button>
            <span className="text-xs min-w-[32px]">
                {conditionIndex > 0 ? "and" : "When"}
            </span>
            <div className="flex w-full gap-2">
                <div className="flex-1">
                    <Select
                        onValueChange={(value) => {
                            form.setValue(`selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}.${value}`, {});
                        }}
                        value={firstQuestionId}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select Question" />
                        </SelectTrigger>
                        <SelectContent>
                            {filteredQuestions.map((question) => (
                                <SelectItem key={question.id} value={question.id}>
                                    {question.question}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-40">
                    <Select
                        onValueChange={(value) => {
                            form.setValue(`selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}.${firstQuestionId}.${value}`, undefined);
                        }}
                        value={selectedType}
                        disabled={typeOptions.length === 0}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Condition type" />
                        </SelectTrigger>
                        <SelectContent>
                            {typeOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                    {option?.replace(/-/g, " ")}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-56">
                    {(() => {
                        if (questionType === 'list') {
                            return (
                                <Combobox
                                    triggerLabel={"Select values"}
                                    mode="multiple"
                                    selectedValue={
                                        form.watch(
                                            `selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}.${selectedQuestionId}.${selectedType}`
                                        )
                                    }
                                    setSelectedValue={(value) => {
                                        form.setValue(`selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}.${selectedQuestionId}.${selectedType}`, value);
                                    }}
                                    inDialog
                                    items={selectedQuestion?.field_options?.map(option => ({ label: option, value: option })) ?? []}
                                />
                            );
                        }
                        else if (questionType === 'number') {
                            return (
                                <Input
                                    value={
                                        form.watch(
                                            `selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}.${selectedQuestionId}.${selectedType}`
                                        )
                                    }
                                    onChange={(e) => {
                                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                                        form.setValue(`selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}.${selectedQuestionId}.${selectedType}`, value);
                                    }}
                                    disabled={!questionType}
                                    type="number"
                                />
                            );
                        } else {
                            return (
                                <Input
                                    value={
                                        form.watch(
                                            `selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}.${firstQuestionId}.${selectedType}`
                                        )
                                    }
                                    onChange={(e) => {
                                        form.setValue(`selected_configuration.conditionals.where.or.${groupIndex}.and.${conditionIndex}.${firstQuestionId}.${selectedType}`, e.target.value);
                                    }}
                                    disabled={!questionType}
                                />
                            );
                        }
                    })()}
                </div>
            </div>
        </div >
    )
}
